/**
 * Showcase manifest — auto-discovery feed for the builder's
 * `/components` gallery (and any future consumer).
 *
 * One ``ShowcaseEntry`` per barrel export the lib intends to surface
 * to users. The matching parity script
 * (``scripts/check-manifest-parity.mjs``) compares this list against
 * the public API in ``src/lib/index.js`` and fails the build if they
 * diverge — so adding/removing a component without updating the
 * gallery is caught at build time.
 *
 * Each render fn receives ``(height, width?, locale?)`` so layout
 * toggles (grid vs full-width) work uniformly and locale-aware demos
 * (date pickers) translate with the gallery host's active language
 * (2026-07-14). Controlled filter / date widgets render through the
 * stateful demo wrappers in ``../showcase/showcase-demos.jsx`` so
 * every entry is fully interactive in the gallery. Entries whose
 * widget opens a popover set ``overflowVisible`` so the gallery card
 * doesn't clip it.
 *
 * Categories drive section grouping in the gallery. ``CATEGORY_ORDER``
 * is the canonical render order.
 */

import { PALETTE, SEMANTIC, InfoTip, TONES, toneStyle } from "./shared.jsx";
import { BrandTokens } from "./brand-panel.jsx";
import { Callout, MetaGrid, MeterBar, MetricStrip, RankedList, StatusChip, ToneIcon } from "./scorecard.jsx";
import {
  // KPIs
  KPICard,
  ChartDataExplorer,
  KPISparkline,
  KPIRing,
  KPIHero,
  KPIBar,
  KPICompare,
  KPICore,
  KPICompact,
  KPIIconStat,
  KPIAccent,
  // Static charts
  VerticalBar,
  HorizontalBar,
  StackedBar,
  StackedShareBar,
  ShareBreakdown,
  IndexMeter,
  GroupedBar,
  DivergingStackedBar,
  LineChart,
  MultiLine,
  StackedArea,
  PieChart,
  ScatterChart,
  BubbleChart,
  RadarChart,
  WaterfallChart,
  FunnelChart,
  TreemapChart,
  HeatmapChart,
  BulletChart,
  LollipopChart,
  ComboChart,
  BarTrend,
  LineTrend,
  Sparkline,
  ProgressRing,
  Gauge,
  // Interactive charts
  VerticalBarT,
  HorizontalBarT,
  LineChartT,
  MultiLineT,
  GroupedBarT,
  ComboChartT,
  StackedShareComboT,
  PieChartT,
  ScatterChartT,
  BubbleChartT,
  // Tables
  HeatmapTable,
  ComparisonTable,
  SparklineTable,
  StatusTable,
  TrendMatrixTable,
  // Dates (filters + pickers render via the stateful demo wrappers
  // in ../showcase/showcase-demos.jsx — imported below)
  FreshnessBadge,
  // Analytics
  CalendarHeatmap,
  TimelineChart,
  AnomalyCard,
  ScoreCard,
  // Maps
  SouthAmericaMap,
  ChileMap,
} from "./index.js";

import {
  DemoEmbeddedFilterBar,
  DemoActiveFiltersBar,
  DemoChipFilter,
  DemoRangeSlider,
  DemoSearchFilter,
  DemoDatePresetFilter,
  DemoAutocompleteFilter,
  DemoDateRangePicker,
  DemoCalendarPicker,
  DemoRelativeDatePicker,
  DemoDateGroupToggle,
  DemoMultiSelectDropdown,
  DemoSegmentedTabs,
  DemoSavedFilters,
  DemoMotionTokens,
  DemoSpinnerLevels,
  DemoGlobalProgressBar,
  DemoRefreshShimmer,
  DemoEntranceGrow,
  DemoEntranceEverywhere,
  DemoLineChartClickFilter,
  DemoMultiLineClickFilter,
  DemoRefetchLanguage,
  DemoSparkleHover,
} from "../showcase/showcase-demos.jsx";

import {
  barData,
  hBarData,
  lineData,
  multiLineSeries,
  multiLineLabels,
  pieData,
  stackedData,
  stackedKeys,
  groupedData,
  groupedKeys,
  scatterData,
  bubbleData,
  radarData,
  waterfallData,
  funnelData,
  treemapData,
  heatmapChartData,
  heatmapChartRows,
  heatmapChartCols,
  bulletData,
  lollipopData,
  stackedAreaSeries,
  stackedAreaLabels,
  tableColumns,
  tableRows,
  sparklineTableColumns,
  sparklineTableRows,
  statusTableColumns,
  statusTableRows,
  trendMatrixColumns,
  trendMatrixRows,
  calendarHeatmapData,
  timelineData,
  timelineMonths,
  scoreBreakdown,
  saMapData,
  clMapData,
  comboData,
  lineTrendData,
  lineTrendForecast,
  sparkData,
  scoreBands,
  meterRows,
  statusChips,
  shareBarRows,
  rankedRisks,
  readoutLines,
  readoutInsights,
  metaItems,
  definitionLines,
  regionStrip,
} from "../showcase/mock-data.js";

// ── Categories ──────────────────────────────────────────────

// Demo text for the widgets whose words are a required prop (DEC-10).
// The gallery is English; a dashboard passes its own translated strings.
const DEMO_MAP_STRINGS = {
  scale: "Scale",
  low: "Low",
  high: "High",
  noData: "No data",
  loading: "Loading map…",
  loadError: "Couldn't load the map boundaries",
};
const DEMO_CALENDAR_STRINGS = { less: "Less", more: "More" };
const DEMO_ANOMALY_STRINGS = { expected: "expected" };

export const CATEGORY_ORDER = [
  // Plan C fase 3 — first on purpose: what a customer can recolour, and
  // what they deliberately cannot, is the frame for reading everything
  // below it.
  "brand",
  "kpis",
  "charts-static",
  "charts-interactive",
  "tables",
  "filters",
  "dates",
  "analytics",
  "maps",
  "report",
  "transitions",
];

export const CATEGORY_LABELS = {
  brand: "Brand — per-tenant colour tokens",
  kpis: "KPI Cards",
  "charts-static": "Charts — Static",
  "charts-interactive": "Charts — Interactive (hover for tooltips)",
  tables: "Tables",
  filters: "Filters",
  dates: "Date Pickers",
  analytics: "Analytics",
  maps: "Geographic Maps",
  report: "Report head and controls",
  transitions: "Transitions — Motion System",
};

/**
 * Spanish section headings. These are the biggest text on the page and
 * they also fill the table of contents, so leaving them English put ten
 * English headings down the middle of a Spanish screen.
 *
 * Only the CATEGORY labels are translated here. Each entry's own title
 * and subtitle stay in English on purpose: they are ~170 strings of
 * component-reference prose, and a machine-shaped translation of that
 * reads worse than the English an admin can already follow. That is
 * tracked as an open item in docs/ui-audit/2026-09/status.md, not as an
 * oversight.
 */
export const CATEGORY_LABELS_ES = {
  brand: "Marca — colores por cliente",
  kpis: "Tarjetas KPI",
  "charts-static": "Gráficos — estáticos",
  "charts-interactive": "Gráficos — interactivos (pasa el cursor para ver el tooltip)",
  tables: "Tablas",
  filters: "Filtros",
  dates: "Selectores de fecha",
  analytics: "Analítica",
  maps: "Mapas geográficos",
  report: "Cabecera y controles del reporte",
  transitions: "Transiciones — sistema de movimiento",
};

/** Portuguese (pt-BR) section headings. Same scope as the Spanish ones:
 *  only the category labels, the entries' own copy stays in English. */
export const CATEGORY_LABELS_PT = {
  brand: "Marca: cores por cliente",
  kpis: "Cartões de KPI",
  "charts-static": "Gráficos: estáticos",
  "charts-interactive": "Gráficos: interativos (passe o cursor para ver o tooltip)",
  tables: "Tabelas",
  filters: "Filtros",
  dates: "Seletores de data",
  analytics: "Analítica",
  maps: "Mapas geográficos",
  report: "Cabeçalho e controles do relatório",
  transitions: "Transições: sistema de movimento",
};

// ── Inline icon (for KPIHero / KPIIconStat) ─────────────────

function DollarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ── Manifest ────────────────────────────────────────────────

/**
 * @typedef {("brand"|"kpis"|"charts-static"|"charts-interactive"|"tables"|"filters"|"dates"|"analytics"|"maps"|"transitions")} ShowcaseCategory
 */

/**
 * @typedef {Object} ShowcaseEntry
 * @property {string} id              kebab-case unique id
 * @property {string} componentName   matches the barrel export name (parity-checked)
 * @property {ShowcaseCategory} category
 * @property {string} title
 * @property {string} subtitle
 * @property {string} [label]
 * @property {(height: number, width?: number) => import('react').ReactNode} render
 * @property {number} [fullHeight]
 * @property {boolean} [customLayout]  entry manages its own layout
 * @property {boolean} [overflowVisible]  widget opens a popover — card must not clip
 */

/** @type {ShowcaseEntry[]} */
/** The showcase shows the GAP on purpose.
 *
 *  A null here is an absent reading, and the gallery is where somebody
 *  sees what that looks like before meeting it in a real report. Drawn
 *  as zero, the line dives to the axis and the heatmap cell takes an end
 *  of the ramp — both read as facts that are not in the data. */
const multiLineWithGap = multiLineSeries.map((s, i) =>
  i === 0
    ? { ...s, data: s.data.map((v, j) => (j === 2 ? null : v)) }
    : s,
);
const heatmapWithGap = heatmapChartData.map((row, i) =>
  row.map((v, j) => (i === 1 && j === 2 ? null : v)),
);

export const SHOWCASE_ENTRIES = [
  // ── Brand tokens (Plan C fase 3) ──────────────────────────
  {
    id: "brand-tokens",
    componentName: "BrandTokens",
    category: "brand",
    title: "Brand colour tokens",
    subtitle:
      "Which colours follow a customer's theme (and which are fixed on purpose). Each themeable swatch reads its own CSS custom property, so a saved theme repaints this panel and every widget below it together.",
    fullHeight: 380,
    customLayout: true,
    render: (h) => <BrandTokens height={h} />,
  },

  // ── KPIs ──────────────────────────────────────────────────
  {
    id: "kpi-card",
    componentName: "KPICard",
    category: "kpis",
    title: "KPI Card",
    subtitle: "Value + subtitle + accent — optionally banded by authored thresholds (epic v3)",
    render: () => (
      <div className="space-y-3">
        <KPICard
          title="Active Listings"
          value="9,650"
          subtitle="+184 this week"
          accent="border-l-accent"
        />
        <KPICard
          title="Authorized listing share"
          value="81.7%"
          accent="border-l-accent"
          valueTone={{ thresholds: scoreBands }}
          valueToneValue={81.7}
          caption={{ text: "Primary health driver", tone: "warn" }}
        />
        <KPICard
          title="SKUs activos"
          value="117"
          subtitle="Catálogo activo de Marca A"
          accent="border-l-accent"
          action={{ label: "Ver en Master Matrix →", href: "#kpi-card" }}
        />
        <KPICard
          title="Win rate"
          value="31.6%"
          subtitle="25 de 79 SKUs comparables al mejor precio"
          accent="border-l-accent"
          action={{
            label: "Ver en Master Matrix →",
            note: "La Master Matrix no está en esta réplica",
          }}
        />
        <div className="text-2xs leading-snug text-neutral-500">
          La segunda card opta por el teñido: el valor toma su color de las
          bandas autoradas y la caption lleva su propio tono. Sin
          <code> valueTone</code> la card renderea exactamente como la primera —
          un número pelado nunca elige un color.
        </div>
        <div className="text-2xs leading-snug text-neutral-500">
          Las dos últimas llevan <code>action</code>, la franja del pie. La
          diferencia entre ellas NO es cosmética: la tercera tiene
          <code> href</code> y es un <code>&lt;a&gt;</code> de verdad con borde
          sólido; la cuarta no lo tiene y renderea DESHABILITADA — borde
          punteado, atenuada, <code>aria-disabled</code>, fuera del orden de
          foco. Un botón que no navega a ninguna parte es peor que no tener
          botón: el lector hace click, no pasa nada, y aprende a desconfiar del
          resto de la página. El <code>note</code> explica por qué y viaja como
          <code> title</code>. Ojo con el alto: la franja cuesta ~30px, así que
          un KPI que la declara suele necesitar una unidad de fila más.
        </div>
      </div>
    ),
  },
  {
    id: "kpi-sparkline",
    componentName: "KPISparkline",
    category: "kpis",
    title: "KPI Sparkline",
    subtitle: "Value + trend mini-line + delta pill",
    render: () => (
      <KPISparkline
        title="Price Changes"
        value="1,234"
        trend="up"
        trendValue="+12.4%"
        sparkData={sparkData}
        color={PALETTE[0]}
      />
    ),
  },
  {
    id: "kpi-ring",
    componentName: "KPIRing",
    category: "kpis",
    title: "KPI Ring",
    subtitle: "Circular progress ring",
    render: () => (
      <KPIRing
        title="MAP Compliance"
        value={88}
        displayValue="88%"
        subtitle="Goal: 90%"
        color={PALETTE[0]}
      />
    ),
  },
  {
    id: "kpi-bar",
    componentName: "KPIBar",
    category: "kpis",
    title: "KPI Bar",
    subtitle: "Horizontal progress bar",
    render: () => (
      <KPIBar
        title="Marketplace Coverage"
        value={73}
        max={100}
        color={PALETTE[0]}
        displayValue="73 / 100"
      />
    ),
  },
  {
    id: "kpi-hero",
    componentName: "KPIHero",
    category: "kpis",
    title: "KPI Hero",
    subtitle: "Big card with gradient + icon",
    render: () => (
      <KPIHero
        title="Total Revenue"
        value="$2.4M"
        subtitle="YTD"
        gradient="from-accent to-accent-dark"
        icon={<DollarIcon />}
      />
    ),
  },
  {
    id: "kpi-compare",
    componentName: "KPICompare",
    category: "kpis",
    title: "KPI Compare",
    subtitle: "Current vs previous period",
    render: () => <KPICompare title="Reactivated" current={342} previous={289} />,
  },
  {
    id: "kpi-core",
    componentName: "KPICore",
    category: "kpis",
    title: "KPI Core",
    subtitle: "Signature-colour card with CORE badge, pp delta, subtitle and action band (DSA strip)",
    render: () => (
      <KPICore
        title="Coverage"
        current={55.1}
        previous={54.6}
        format="percent"
        badge="CORE"
        color={PALETTE[4]}
        subtitle="Listed / universe"
        action={{ label: "See retailers →", href: "#retailers" }}
      />
    ),
  },
  {
    id: "kpi-compact",
    componentName: "KPICompact",
    category: "kpis",
    title: "KPI Compact",
    subtitle: "Dense single-row layout; the ⓘ follows the title and a long value drops below it",
    render: () => (
      <div className="space-y-3">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-2 divide-y divide-neutral-50">
          {[
            ["Laptops", 3400, 8.2],
            ["Printers", 2800, -2.1],
            ["Monitors", 2100, 4.7],
          ].map(([title, value, change], i) => (
            <KPICompact
              key={title}
              title={title}
              value={value.toLocaleString()}
              change={change}
              color={PALETTE[i]}
              trailing={<InfoTip text="Units sold in the period, all channels." />}
            />
          ))}
        </div>
        <div className="max-w-[14rem]">
          <KPICompact
            title="Total annual revenue"
            value="US$ 425,659,276"
            trailing={<InfoTip text="A value too wide for one line goes below the label, never over it." />}
          />
        </div>
        <div className="grid max-w-[30rem] grid-cols-2 gap-3">
          {[["Total annual revenue", "US$ 425,659,276"], ["Missing SKUs", "626"]].map(([title, value]) => (
            <KPICompact
              key={title}
              stacked
              title={title}
              value={value}
              trailing={<InfoTip text="stacked: label on top, value below, ⓘ inline after the label." />}
            />
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "kpi-icon-stat",
    componentName: "KPIIconStat",
    category: "kpis",
    title: "KPI Icon Stat",
    subtitle: "Value with icon in tinted box",
    render: () => (
      <KPIIconStat
        title="Active Alerts"
        value="12"
        subtitle="3 critical"
        iconColor="text-accent-dark"
        icon={<AlertIcon />}
      />
    ),
  },
  {
    id: "kpi-accent",
    componentName: "KPIAccent",
    category: "kpis",
    title: "KPI Accent",
    subtitle: "White card, colour band and tinted icon",
    render: () => (
      <KPIAccent
        title="SKU caídos hoy"
        value="38"
        subtitle="▼ −5 vs 20 oct"
        color={PALETTE[1]}
        icon={<AlertIcon />}
      />
    ),
  },

  // ── Static charts ─────────────────────────────────────────
  {
    id: "vertical-bar",
    componentName: "VerticalBar",
    category: "charts-static",
    title: "Vertical Bars",
    subtitle: "Listings per month",
    render: (h, w) => <VerticalBar data={barData} height={h} width={w} />,
  },
  {
    id: "horizontal-bar",
    componentName: "HorizontalBar",
    category: "charts-static",
    title: "Horizontal Bars",
    subtitle:
      "Ranking con valor y share — dos densidades: cómoda (28px, con chip de rank) y compacta (19px, sin chip)",
    render: (h, w) => (
      <div className="space-y-4">
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
            comfortable · default
          </p>
          <HorizontalBar data={hBarData} height={Math.max(120, (h || 260) * 0.5)} width={w} />
        </div>
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
            compact
          </p>
          <HorizontalBar
            data={hBarData}
            density="compact"
            height={Math.max(90, (h || 260) * 0.34)}
            width={w}
          />
        </div>
        <div className="text-2xs leading-snug text-neutral-500">
          Las mismas filas, la misma data. En compacta la fila baja de 28 a 19px
          y el chip de rank numerado NO se dibuja — el orden de las filas ya ES
          el ranking, así que el chip lo repetía, y sus 28px de ancho vuelven al
          canal de labels, que era el otro apretado. Siete filas pasan de 212px
          de alto natural a 149: eso es lo que evita que una tarjeta de ranking
          estire su fila entera y deje a las vecinas con cien y pico de píxeles
          de blanco. Los labels que no entran se TRUNCAN en vez de envolver (una
          fila de 19px no da para dos líneas) y el label completo queda en el
          <code> title</code>. Valor, share % y label no se pierden en ninguna
          de las dos.
        </div>
      </div>
    ),
  },
  {
    id: "line-chart",
    componentName: "LineChart",
    category: "charts-static",
    title: "Line",
    subtitle: "Price-change events",
    render: (h, w) => <LineChart data={lineData} height={h} width={w} />,
  },
  {
    id: "multi-line",
    componentName: "MultiLine",
    category: "charts-static",
    title: "Multi-line",
    subtitle: "Listings tracked by category",
    render: (h, w) => (
      <MultiLine series={multiLineSeries} labels={multiLineLabels} height={h} width={w} />
    ),
  },
  {
    id: "stacked-bar",
    componentName: "StackedBar",
    category: "charts-static",
    title: "Stacked Bars",
    subtitle: "Quarterly by category",
    render: (h, w) => (
      <StackedBar data={stackedData} keys={stackedKeys} height={h} width={w} />
    ),
  },
  {
    id: "grouped-bar",
    componentName: "GroupedBar",
    category: "charts-static",
    title: "Grouped Bars",
    subtitle: "YoY listings (2024 vs 2025)",
    render: (h, w) => (
      <GroupedBar data={groupedData} keys={groupedKeys} height={h} width={w} />
    ),
  },
  {
    id: "diverging-stacked-bar",
    componentName: "DivergingStackedBar",
    category: "charts-static",
    title: "Diverging Stacked Bars",
    subtitle: "In / Out flow by category",
    render: (h, w) => (
      <DivergingStackedBar
        data={[
          // `in`/`out` are positive magnitudes (same contract as
          // GenericChart) — the component renders `out` below zero.
          { label: "Jan", in: { laptops: 12, printers: 8 }, out: { laptops: 5, printers: 3 } },
          { label: "Feb", in: { laptops: 18, printers: 11 }, out: { laptops: 7, printers: 4 } },
          { label: "Mar", in: { laptops: 22, printers: 14 }, out: { laptops: 29, printers: 6 } },
          { label: "Apr", in: { laptops: 15, printers: 9 }, out: { laptops: 4, printers: 2 } },
        ]}
        keys={["laptops", "printers"]}
        height={h}
        width={w}
      />
    ),
  },
  {
    id: "stacked-area",
    componentName: "StackedArea",
    category: "charts-static",
    title: "Stacked Area",
    subtitle: "Trend by category",
    render: (h, w) => (
      <StackedArea series={stackedAreaSeries} labels={stackedAreaLabels} height={h} width={w} />
    ),
  },
  {
    id: "pie",
    componentName: "PieChart",
    category: "charts-static",
    title: "Pie",
    subtitle: "Listings share by category · pie + legend centred as one block in a wide card (open full size)",
    fullHeight: 400,
    render: (h, w) => <PieChart data={pieData} height={h} width={w} />,
  },
  {
    id: "scatter",
    componentName: "ScatterChart",
    category: "charts-static",
    title: "Scatter",
    subtitle: "SKUs: price index vs compliance",
    render: (h, w) => <ScatterChart data={scatterData} height={h} width={w} />,
  },
  {
    id: "bubble",
    componentName: "BubbleChart",
    category: "charts-static",
    title: "Bubble",
    subtitle: "Marketplaces: price/compl./size",
    render: (h, w) => <BubbleChart data={bubbleData} height={h} width={w} />,
  },
  {
    id: "radar",
    componentName: "RadarChart",
    category: "charts-static",
    title: "Radar",
    subtitle: "Brand health scorecard",
    fullHeight: 440,
    render: (h, w) => <RadarChart data={radarData} height={h} width={w} />,
  },
  {
    id: "waterfall",
    componentName: "WaterfallChart",
    category: "charts-static",
    title: "Waterfall",
    subtitle: "Listings flow (open → close)",
    render: (h, w) => <WaterfallChart data={waterfallData} height={h} width={w} />,
  },
  {
    id: "funnel",
    componentName: "FunnelChart",
    category: "charts-static",
    title: "Funnel",
    subtitle: "Marketplace activation funnel",
    render: (h, w) => <FunnelChart data={funnelData} height={h} width={w} />,
  },
  {
    id: "treemap",
    componentName: "TreemapChart",
    category: "charts-static",
    title: "Treemap",
    subtitle: "Listings share by product line",
    render: (h, w) => <TreemapChart data={treemapData} height={h} width={w} />,
  },
  {
    id: "heatmap-chart",
    componentName: "HeatmapChart",
    category: "charts-static",
    title: "Heatmap",
    subtitle: "Activity by weekday × month — with one absent reading",
    render: (h, w) => (
      <HeatmapChart
        data={heatmapWithGap}
        rows={heatmapChartRows}
        cols={heatmapChartCols}
        yLabels={heatmapChartRows}
        xLabels={heatmapChartCols}
        height={h}
        width={w}
      />
    ),
  },
  {
    id: "bullet",
    componentName: "BulletChart",
    category: "charts-static",
    title: "Bullet",
    subtitle: "KPI vs target — default amber miss, or semantic red via missColor",
    render: (h, w) => (
      <div className="space-y-2">
        <BulletChart data={bulletData} height={Math.round(h * 0.55)} width={w} />
        <div className="text-2xs leading-snug text-neutral-500">
          Con <code>missColor</code> = rojo profundo de bandas: así lo pasan
          los scorecards — teal cumple el target, rojo profundo no llega
          (par colour-blind-friendly, 2026-07-21).
        </div>
        <BulletChart
          data={bulletData}
          height={Math.round(h * 0.45)}
          width={w}
          missColor={SEMANTIC.bandNegative}
        />
        <div className="text-2xs leading-snug text-neutral-500">
          Una fila sin meta o sin máximo muestra el aviso, no una escala
          inventada:
        </div>
        <BulletChart data={[{ label: "Sin máximo", value: 40, target: 50 }]} height={60} width={w} />
      </div>
    ),
  },
  {
    id: "lollipop",
    componentName: "LollipopChart",
    category: "charts-static",
    title: "Lollipop",
    subtitle: "Top SKUs by # ASINs — long labels wrap to two lines",
    render: (h, w) => <LollipopChart data={lollipopData} height={h} width={w} />,
  },
  {
    id: "combo",
    componentName: "ComboChart",
    category: "charts-static",
    title: "Combo Bar + Line",
    subtitle: "Listings & MAP compliance",
    render: (h, w) => (
      <ComboChart
        data={comboData}
        barKey="listings"
        lineKey="compliance"
        barLabel="Listings"
        lineLabel="MAP %"
        barColor={PALETTE[0]}
        lineColor={PALETTE[2]}
        height={h}
        width={w}
      />
    ),
  },
  {
    id: "bar-trend",
    componentName: "BarTrend",
    category: "charts-static",
    title: "Bar + Trend",
    subtitle: "Linear regression overlay",
    render: (h, w) => (
      <BarTrend data={barData} color={PALETTE[0]} trendColor={PALETTE[3]} height={h} width={w} />
    ),
  },
  {
    id: "line-trend",
    componentName: "LineTrend",
    category: "charts-static",
    title: "Line + Forecast",
    subtitle: "Historical line + projection",
    render: (h, w) => (
      <LineTrend
        data={lineTrendData}
        forecast={lineTrendForecast}
        color={PALETTE[0]}
        height={h}
        width={w}
      />
    ),
  },
  {
    id: "sparkline",
    componentName: "Sparkline",
    category: "charts-static",
    title: "Sparkline",
    subtitle: "Micro-line",
    render: (_h, w) => (
      <div className="flex items-center justify-center py-6">
        <Sparkline data={sparkData} color={PALETTE[0]} width={w ? Math.min(w, 220) : 160} height={32} />
      </div>
    ),
  },
  {
    id: "progress-ring",
    componentName: "ProgressRing",
    category: "charts-static",
    title: "Progress Ring",
    subtitle: "Standalone circular progress",
    render: () => (
      <div className="flex items-center justify-center py-6">
        <ProgressRing value={78} max={100} color={PALETTE[0]} size={64} strokeWidth={6} />
      </div>
    ),
  },
  {
    id: "gauge",
    componentName: "Gauge",
    category: "charts-static",
    title: "Gauge",
    subtitle: "Arc gauge",
    render: () => (
      <div className="flex items-center justify-center py-4">
        <Gauge value={72} max={100} color={PALETTE[0]} height={110} />
      </div>
    ),
  },

  {
    id: "stacked-share-bar",
    componentName: "StackedShareBar",
    category: "charts-static",
    title: "Stacked Share Bar",
    subtitle:
      "One row, 100% normalized — hover a segment for its breakdown; refuses to draw when a bucket is missing",
    render: () => (
      <div className="space-y-3">
        {shareBarRows.map((row) => (
          <div key={row.label} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-neutral-700">{row.label}</span>
              <span className="text-neutral-600">
                {row.segments.some((seg) => seg.value == null)
                  ? "1P sin dato — bloqueado"
                  : "MPA / Non-MPA / 1P / HHO"}
              </span>
            </div>
            <StackedShareBar
              segments={row.segments}
              ariaLabel={`${row.label} authorization mix`}
            />
          </div>
        ))}
        <div className="pt-1 text-2xs leading-snug text-neutral-500">
          Hover sobre un segmento: tooltip con label · share · valor, y los
          demás segmentos se atenúan (2026-07). La tercera fila tiene un
          bucket en null: en una barra normalizada, descartarlo repartiría su
          share entre los demás tan inventadamente como forzarlo a 0, así que
          el default se niega a dibujar y nombra la clave. El segmento HHO
          (0.2%) sigue visible gracias a minSegmentPx.
        </div>
      </div>
    ),
  },

  {
    id: "index-meter",
    componentName: "IndexMeter",
    category: "charts-static",
    title: "Index Meter",
    subtitle:
      "Un valor sobre una escala con referencia y banda — el marcador es un rombo, la referencia una línea de guiones",
    render: () => (
      <div className="space-y-5">
        <IndexMeter
          value={92}
          min={70}
          max={130}
          band={{ from: 85, to: 104, label: "Banda P25–P75 (85–104)" }}
          reference={{ value: 100, label: "100" }}
          unit="índice"
          note="Marca A está 8% más barata que el promedio del mercado."
          delta={-2}
          deltaLabel="vs trimestre anterior"
          deltaGoodWhen="down"
          minLabel="70 · más barato"
          maxLabel="más caro · 130"
          height={120}
        />
        <IndexMeter
          value={141}
          min={70}
          max={130}
          band={{ from: 85, to: 104 }}
          reference={{ value: 100, label: "100" }}
          unit="índice"
          note="Fuera de escala: el rombo se ancla al borde con un caret ›, y el número impreso sigue siendo el real."
          delta={3}
          deltaLabel="vs trimestre anterior"
          deltaGoodWhen="down"
          height={110}
        />
        <div className="text-2xs leading-snug text-neutral-500">
          Tres canales distintos y ninguno depende del tono: la referencia es una
          línea de GUIONES con su número, la banda va bracketeada por dos ticks y
          rotulada en texto, y el valor es un ROMBO sobre un tallo. Una escala
          invertida o un valor no numérico no dibujan nada: renderean el vacío
          nombrando el problema. El delta (puntos del índice contra el período
          que nombra `deltaLabel`) es el `DeltaChip` compartido, y su color sale
          SOLO de `deltaGoodWhen`: en un índice de precio bajar es abaratarse
          (verde), subir es encarecerse (rojo); sin esa declaración el chip queda
          neutro con su flecha.
        </div>
      </div>
    ),
  },

  {
    id: "share-breakdown",
    componentName: "ShareBreakdown",
    category: "charts-static",
    title: "Share Breakdown",
    subtitle:
      "Barra 100% apilada + su propia tabla de lectura (n de N); el conteo entra en el segmento que le da el ancho",
    render: () => (
      <div className="space-y-4">
        <ShareBreakdown
          segments={[
            { key: "ganando", label: "Ganando", value: 18, color: toneStyle("good").fill },
            { key: "empatado", label: "Empatado", value: 5, color: toneStyle("info_light").fill },
            { key: "dentro3", label: "Dentro del 3%", value: 2, color: toneStyle("info").fill },
            { key: "atras310", label: "3–10% atrás", value: 7, color: toneStyle("warn").fill },
            { key: "atras10", label: "10%+ atrás", value: 14, color: toneStyle("bad").fill },
            { key: "sincomp", label: "Sin competencia", value: 25, color: toneStyle("muted").fill },
            { key: "ausente", label: "Ausente", value: 439, color: toneStyle("neutral").fill },
          ]}
          totalLabel="Segmentos"
          height={240}
        />
        <div className="rounded-lg border border-dashed border-neutral-300 p-2" style={{ height: 300 }}>
          <ShareBreakdown
            fill
            segments={[
              { key: "available", label: "Disponible", value: 2974, color: toneStyle("good").fill },
              { key: "oos", label: "Sin inventario", value: 18, color: toneStyle("warn").fill },
              { key: "fallen", label: "Caído de la PLP", value: 584, color: toneStyle("bad").fill },
              { key: "unread", label: "Publicado · sin lectura de stock", value: 2187, color: toneStyle("neutral").fill },
              { key: "never", label: "Nunca publicado", value: 15045, color: toneStyle("info").fill },
            ]}
            totalLabel="obs."
          />
        </div>
        <div className="text-2xs leading-snug text-neutral-500">
          Con <code>fill</code> (abajo, dentro de una caja de 300px) toma el alto
          completo del contenedor: barra más gruesa y filas repartidas, en vez de
          quedar arriba con la tarjeta vacía debajo. Es lo que hace en una celda
          del dashboard. Una sola declaración de segmentos pinta la barra Y la tabla, así que la
          leyenda no puede desalinearse del dibujo. Hereda la política de
          `StackedShareBar`: un bucket sin dato bloquea la barra entera en vez de
          repartir su share entre los sobrevivientes. Si le pasas `total` y no
          coincide con la suma, no elige uno — muestra ambos y falla.
        </div>
      </div>
    ),
  },

  // ── Interactive charts (T variants) ───────────────────────
  {
    id: "vertical-bar-t",
    componentName: "VerticalBarT",
    category: "charts-interactive",
    title: "Vertical Bars",
    subtitle: "Hover for tooltip",
    render: (h, w) => <VerticalBarT data={barData} height={h} width={w} />,
  },
  {
    id: "horizontal-bar-t",
    componentName: "HorizontalBarT",
    category: "charts-interactive",
    title: "Horizontal Bars",
    subtitle:
      "Hover for tooltip · long labels wrap to 2 lines · el share-of-total se apaga con showShare={false}",
    customLayout: true,
    render: () => (
      <div className="space-y-4">
        <div>
          <p className="mb-1 text-[11px] font-semibold text-neutral-500">
            Por defecto — los valores SON partes de un todo (conteos, montos):
            el % del total informa.
          </p>
          <HorizontalBarT data={hBarData} height={200} width={520} />
        </div>
        <div>
          <p className="mb-1 text-[11px] font-semibold text-neutral-500">
            showShare={"{false}"} — índice, precio promedio, ratio o puntaje:
            no son aditivos, así que el % del total no es ninguna cantidad real.
          </p>
          <HorizontalBarT
            data={[
              { label: "MARCA A", value: 72 },
              { label: "MARCA D", value: 66 },
              { label: "MARCA B", value: 130 },
              { label: "MARCA C", value: 159 },
            ]}
            showShare={false}
            height={160}
            width={520}
          />
        </div>
      </div>
    ),
  },
  {
    id: "line-chart-t",
    componentName: "LineChartT",
    category: "charts-interactive",
    title: "Line",
    subtitle: "Crosshair on hover · clic en un punto = cross-filter (semana activa marcada por forma)",
    customLayout: true,
    render: () => <DemoLineChartClickFilter />,
  },
  {
    id: "multi-line-t",
    componentName: "MultiLineT",
    category: "charts-interactive",
    title: "Multi-line",
    subtitle: "Multi-series crosshair · clic en un punto = cross-filter",
    customLayout: true,
    render: () => <DemoMultiLineClickFilter />,
  },
  {
    id: "grouped-bar-t",
    componentName: "GroupedBarT",
    category: "charts-interactive",
    title: "Grouped Bars",
    subtitle: "Tooltip per group",
    render: (h, w) => (
      <GroupedBarT data={groupedData} keys={groupedKeys} height={h} width={w} />
    ),
  },
  {
    id: "combo-chart-t",
    componentName: "ComboChartT",
    category: "charts-interactive",
    title: "Combo Bar + Line",
    subtitle: "Dual-axis tooltip",
    render: (h, w) => (
      <ComboChartT
        data={comboData}
        barKey="listings"
        lineKey="compliance"
        barLabel="Listings"
        lineLabel="MAP %"
        barColor={PALETTE[0]}
        lineColor={PALETTE[2]}
        height={h}
        width={w}
      />
    ),
  },
  {
    id: "stacked-share-combo-t",
    componentName: "StackedShareComboT",
    category: "charts-interactive",
    title: "Stacked Share Combo",
    subtitle:
      "100% mix (banda daltónica) + línea de volumen en eje derecho — hover con tooltip multi-serie y guideline",
    render: (h, w) => (
      <StackedShareComboT
        data={Array.from({ length: 104 }, (_, i) => {
          const t = i / 103;
          return {
            label: `W${String(i + 1).padStart(3, "0")}`,
            // Improving mix crossing the 90% target near the end +
            // one MISSING week (null ⇒ no column, never a zero).
            value: i === 40 ? null : Math.min(0.97, 0.72 + 0.24 * t + 0.02 * Math.sin(i / 4)),
            total: Math.round(4200 * (0.7 + 0.6 * t + 0.1 * Math.sin(i / 6))),
          };
        })}
        target={0.9}
        height={h}
        width={w}
      />
    ),
  },
  {
    id: "pie-chart-t",
    componentName: "PieChartT",
    category: "charts-interactive",
    title: "Pie / Donut",
    subtitle: "Hover for percentage · donut + legend centred as one block in a wide card (open full size)",
    fullHeight: 400,
    render: (h, w) => <PieChartT data={pieData} donut height={h} width={w} />,
  },
  {
    id: "scatter-chart-t",
    componentName: "ScatterChartT",
    category: "charts-interactive",
    title: "Scatter",
    subtitle: "Hover for point details",
    render: (h, w) => <ScatterChartT data={scatterData} height={h} width={w} />,
  },
  {
    id: "bubble-chart-t",
    componentName: "BubbleChartT",
    category: "charts-interactive",
    title: "Bubble",
    subtitle: "Hover for x/y/size",
    render: (h, w) => <BubbleChartT data={bubbleData} height={h} width={w} />,
  },

  // ── Tables ────────────────────────────────────────────────
  {
    id: "heatmap-table",
    componentName: "HeatmapTable",
    category: "tables",
    title: "Heatmap Table",
    subtitle: "Numeric cells tinted by column min/max",
    customLayout: true,
    render: () => (
      <HeatmapTable
        title="Heatmap Table — 16 retailers"
        columns={tableColumns}
        rows={tableRows}
        heatColumns={["listings", "compliance", "coverage"]}
        deltaColumns={["change"]}
        pageSize={10}
        zebra
      />
    ),
  },
  {
    id: "comparison-table",
    componentName: "ComparisonTable",
    category: "tables",
    title: "Comparison Table",
    subtitle: "Signed delta column with up/down icons",
    customLayout: true,
    render: () => (
      <ComparisonTable
        title="Comparison Table"
        columns={tableColumns}
        rows={tableRows}
        compareKeys={["change"]}
        pageSize={10}
      />
    ),
  },
  {
    id: "sparkline-table",
    componentName: "SparklineTable",
    category: "tables",
    title: "Sparkline Table",
    subtitle: "Inline trend per row",
    customLayout: true,
    render: () => (
      <SparklineTable
        title="Sparkline Table"
        columns={sparklineTableColumns}
        rows={sparklineTableRows}
        sparkColumn="trend"
        deltaColumns={["growth"]}
        pageSize={5}
      />
    ),
  },
  {
    id: "status-table",
    componentName: "StatusTable",
    category: "tables",
    title: "Status Table",
    subtitle: "Colored status badges + progress",
    customLayout: true,
    render: () => (
      <StatusTable
        title="Status Table"
        columns={statusTableColumns}
        rows={statusTableRows}
        statusColumn="status"
        progressColumn="progress"
        deltaColumns={["delta"]}
        pageSize={5}
        zebra
      />
    ),
  },
  {
    id: "trend-matrix-table",
    componentName: "TrendMatrixTable",
    category: "tables",
    title: "Trend Matrix Table",
    subtitle: "Value + micro-bar + Δ chip per period cell (fiscal comparison)",
    customLayout: true,
    render: () => (
      <TrendMatrixTable
        title="Comparativa por trimestre fiscal"
        subtitle="micro-barras = magnitud relativa · chips = variación vs trimestre anterior · (*) en curso"
        columns={trendMatrixColumns}
        rows={trendMatrixRows}
      />
    ),
  },

  // ── Filters ───────────────────────────────────────────────
  {id: "chart-data-explorer", componentName: "ChartDataExplorer", category: "charts-interactive", title: "Chart data", subtitle: "Optional data table for every chart family", render: (h) => <ChartDataExplorer rows={barData} label="Ver datos"><VerticalBarT data={barData} height={h}/></ChartDataExplorer>},
  {id: "filter-disclosure", componentName: "FilterDisclosure", category: "filters", title: "Filter Disclosure", subtitle: "Shared expand, focus and inert behavior", render: () => <DemoEmbeddedFilterBar />},
  {
    id: "embedded-filter-bar",
    componentName: "EmbeddedFilterBar",
    category: "filters",
    title: "Embedded Filter Bar",
    subtitle: "Compact dark dropdowns with collapse",
    customLayout: true,
    overflowVisible: true,
    render: () => <DemoEmbeddedFilterBar />,
  },
  {
    id: "active-filters-bar",
    componentName: "ActiveFiltersBar",
    category: "filters",
    title: "Active Filters Bar",
    subtitle: "Removable filter pills",
    customLayout: true,
    render: () => <DemoActiveFiltersBar />,
  },
  {
    id: "multi-select-dropdown",
    componentName: "MultiSelectDropdown",
    category: "filters",
    title: "Multi-select Dropdown",
    subtitle: "Checkbox menu for selecting multiple values",
    customLayout: true,
    overflowVisible: true,
    render: () => <DemoMultiSelectDropdown />,
  },
  {
    id: "chip-filter",
    componentName: "ChipFilter",
    category: "filters",
    title: "Chip Filter",
    subtitle: "Toggleable category pills",
    customLayout: true,
    render: () => <DemoChipFilter />,
  },
  {
    id: "range-slider",
    componentName: "RangeSlider",
    category: "filters",
    title: "Range Slider",
    subtitle: "Numeric range picker",
    customLayout: true,
    render: () => <DemoRangeSlider />,
  },
  {
    id: "search-filter",
    componentName: "SearchFilter",
    category: "filters",
    title: "Search Filter",
    subtitle: "Free-text contains",
    customLayout: true,
    render: () => <DemoSearchFilter />,
  },
  {
    id: "date-preset-filter",
    componentName: "DatePresetFilter",
    category: "filters",
    title: "Date Preset Filter",
    subtitle: "Quick relative-time pills",
    customLayout: true,
    render: () => <DemoDatePresetFilter />,
  },
  {
    id: "autocomplete-filter",
    componentName: "AutocompleteFilter",
    category: "filters",
    title: "Autocomplete Filter",
    subtitle: "Combobox with suggestions",
    customLayout: true,
    overflowVisible: true,
    render: () => <DemoAutocompleteFilter />,
  },

  {
    id: "segmented-tabs",
    componentName: "SegmentedTabs",
    category: "filters",
    title: "Segmented Tabs",
    subtitle: "Single-select tab row — radiogroup semantics, arrows move the selection",
    customLayout: true,
    render: () => <DemoSegmentedTabs />,
  },

  // ── Dates ─────────────────────────────────────────────────
  {
    id: "date-range-picker",
    componentName: "DateRangePicker",
    category: "dates",
    title: "Date Range Picker",
    subtitle:
      "Dual calendar with preset chips + in-popover zoom slider — re-picking the start keeps the end (it only clears when start > end)",
    customLayout: true,
    overflowVisible: true,
    render: (_h, _w, locale) => <DemoDateRangePicker locale={locale} />,
  },
  {
    id: "calendar-picker",
    componentName: "CalendarPicker",
    category: "dates",
    title: "Calendar Picker",
    subtitle: "Single date selector",
    customLayout: true,
    overflowVisible: true,
    render: (_h, _w, locale) => <DemoCalendarPicker locale={locale} />,
  },
  {
    id: "relative-date-picker",
    componentName: "RelativeDatePicker",
    category: "dates",
    title: "Relative Date Picker",
    subtitle: "Last N days / weeks / months",
    customLayout: true,
    overflowVisible: true,
    render: (_h, _w, locale) => <DemoRelativeDatePicker locale={locale} />,
  },
  {
    id: "date-group-toggle",
    componentName: "DateGroupToggle",
    category: "dates",
    title: "Date Group Toggle",
    subtitle: "Day / Week / Month / Quarter switch",
    customLayout: true,
    render: (_h, _w, locale) => <DemoDateGroupToggle locale={locale} />,
  },
  {
    id: "freshness-badge",
    componentName: "FreshnessBadge",
    category: "dates",
    title: "Freshness Badge",
    subtitle: "Pill with last-updated status",
    customLayout: true,
    render: () => (
      <div className="flex gap-3 p-2">
        <FreshnessBadge status="fresh" lastUpdated="Live" />
        <FreshnessBadge status="stale" lastUpdated="2h ago" />
        <FreshnessBadge status="error" lastUpdated="Error" />
      </div>
    ),
  },

  // ── Analytics ─────────────────────────────────────────────
  {
    id: "calendar-heatmap",
    componentName: "CalendarHeatmap",
    category: "analytics",
    title: "Calendar Heatmap",
    subtitle: "Year-long density grid by day",
    customLayout: true,
    render: () => (
      <CalendarHeatmap data={calendarHeatmapData} year={2025} locale="en" strings={DEMO_CALENDAR_STRINGS} />
    ),
  },
  {
    id: "timeline-chart",
    componentName: "TimelineChart",
    category: "analytics",
    title: "Timeline",
    subtitle: "Gantt-style phase rows",
    customLayout: true,
    render: () => <TimelineChart data={timelineData} months={timelineMonths} />,
  },
  {
    id: "anomaly-card",
    componentName: "AnomalyCard",
    category: "analytics",
    title: "Anomaly Card",
    subtitle: "Severity-toned alert card",
    render: () => (
      <AnomalyCard
        title="Listing volume"
        value={4123}
        expected={1200}
        severity="critical"
        description="+243% above 30-day baseline"
        timestamp="3h ago"
        locale="en"
        strings={DEMO_ANOMALY_STRINGS}
      />
    ),
  },
  {
    id: "score-card",
    componentName: "ScoreCard",
    category: "analytics",
    title: "Score Card",
    subtitle: "Composite score with breakdown",
    render: () => (
      <ScoreCard
        title="Brand Health"
        score={82}
        maxScore={100}
        breakdown={scoreBreakdown}
        color={PALETTE[0]}
      />
    ),
  },
  {
    id: "saved-filters",
    componentName: "SavedFilters",
    category: "analytics",
    title: "Saved Filters",
    subtitle: "User-saved filter presets",
    customLayout: true,
    render: () => <DemoSavedFilters />,
  },

  {
    id: "meter-bar",
    componentName: "MeterBar",
    category: "analytics",
    title: "Meter Bar",
    subtitle: "Track + fill banded by authored thresholds — hover for the reading",
    render: () => (
      <div className="space-y-3">
        {meterRows.map((row) => (
          <MeterBar
            key={row.label}
            label={row.label}
            labelWidth={72}
            value={row.value}
            toneSpec={{ thresholds: scoreBands }}
            showValue
            ariaLabel={`${row.label} marketplace health`}
            toneLabel={row.value == null ? "" : "banded"}
            hoverText={row.value == null ? undefined : `${row.label} · ${row.value}`}
          />
        ))}
        <div className="pt-1 text-2xs leading-snug text-neutral-500">
          Same bands, three tones — hover a track for its cursor tooltip
          (<code>hoverText</code>, opt-in). A null row renders an empty track
          with an em-dash, never a zero. Drop <code>toneSpec</code> and every
          bar goes neutral: a bare number never picks a colour.
        </div>
        <MeterBar
          value={64}
          toneSpec={{ tone: "neutral" }}
          height={6}
          ariaLabel="Untinted meter"
        />
      </div>
    ),
  },
  {
    id: "tone-icon",
    componentName: "ToneIcon",
    category: "analytics",
    title: "Tone Icon",
    subtitle: "Band state as a SHAPE (✓/!/×) — survives red-green colour-blindness",
    render: () => (
      <div className="space-y-3">
        <div className="flex items-center gap-6">
          {TONES.map((tone) => (
            <div key={tone} className="flex items-center gap-2">
              <ToneIcon tone={tone} size={20} />
              <span className="text-xs text-neutral-500">{tone}</span>
            </div>
          ))}
        </div>
        <div className="text-2xs leading-snug text-neutral-500">
          El estado viaja por FORMA además de color (el dueño del producto es
          daltónico rojo-verde): ✓ círculo, ! triángulo, × círculo, – neutro.
          Tintado con la paleta de bandas colour-blind-friendly
          (<code>brand.semantic.band*</code>: teal vs rojo profundo) — legible
          también en escala de grises.
        </div>
      </div>
    ),
  },
  {
    id: "status-chip",
    componentName: "StatusChip",
    category: "analytics",
    title: "Status Chip",
    subtitle: "Pastel pill in one threshold tone — arbitrary text, no delta semantics",
    render: () => (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {statusChips.map((chip) => (
            <StatusChip
              key={chip.label}
              label={chip.label}
              value={chip.value}
              toneSpec={{ thresholds: scoreBands }}
              toneLabel="banded"
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip label="High" toneSpec={{ tone: "bad" }} size="xs" />
          <StatusChip label="Medium" toneSpec={{ tone: "warn" }} size="sm" />
          <StatusChip label="Low" toneSpec={{ tone: "good" }} size="md" />
          <StatusChip label="Unrated" />
        </div>
        <div className="text-2xs leading-snug text-neutral-500">
          Top row bands a score; bottom row states the tone outright. The band
          name also rides into the accessible name, so the judgement survives
          without colour.
        </div>
      </div>
    ),
  },

  {
    id: "meta-grid",
    componentName: "MetaGrid",
    category: "analytics",
    title: "Meta Grid",
    subtitle: "Report chrome and definitional lines — the only place the reader learns what a number means",
    render: () => (
      <div className="space-y-3">
        <MetaGrid items={metaItems} columns={2} />
        <MetaGrid items={definitionLines} columns={1} bordered={false} />
      </div>
    ),
  },
  {
    id: "ranked-list",
    componentName: "RankedList",
    category: "analytics",
    title: "Ranked List",
    subtitle: "40 rows, windowed and scrollable — never sliced",
    render: () => (
      <div className="space-y-3">
        <RankedList
          items={rankedRisks}
          maxHeight={220}
          ariaLabel="Top global risks"
        />
        <div className="text-2xs leading-snug text-neutral-500">
          Son 40 filas y las 40 se alcanzan scrolleando: el DOM se ventanea,
          la data nunca se corta. El scrollbar representa el universo completo.
        </div>
        <RankedList items={readoutLines} layout="stack" ariaLabel="Executive readout" />
        <div className="text-2xs leading-snug text-neutral-500">
          Extras opcionales: punto de color, valor grande con tono y botón por fila;
          `marker="up"` cambia el número por una flecha.
        </div>
        <RankedList
          ariaLabel="Where you are losing"
          items={[
            { id: "a", title: "Retailer A", subtitle: "today: 12 available · 3 fell off", dot: PALETTE[0], value: "31.0%", valueTone: "bad", action: { label: "View", onClick: () => {} } },
            { id: "b", title: "Retailer B", subtitle: "today: 40 available · 1 fell off", dot: PALETTE[1], value: "52.4%", valueTone: "warn", action: { label: "View", onClick: () => {} } },
          ]}
        />
        <RankedList
          marker="up"
          ariaLabel="Where you can win"
          items={[{ id: "c", title: "Brand X · Model 55", subtitle: "Retailer A · TV · out for 4 days", chip: { label: "Fell off", tone: "bad" } }]}
        />
      </div>
    ),
  },
  {
    id: "callout",
    componentName: "Callout",
    category: "analytics",
    title: "Callout",
    subtitle: "Authored tinted surface — two colour axes, and an AI marker that reads as a sentence",
    render: () => (
      <div className="space-y-3">
        <Callout tone="bad" role="alert" icon={<span>!</span>} title="Action needed" aiGenerated>
          Best Buy - Print HW at 61% approved, the lowest score and steepest
          decline across all category/marketplace combinations.
        </Callout>
        {/* Mirrors the production `ai_readout` block: the marker is
            SPELLED OUT (a two-letter chip is skimmed past on a
            full-width band of prose), and the insights are prose, not
            RankedList subtitles — that component's caption styling
            lands at ~2.2:1 against this lavender. */}
        <Callout accent="info" title="Executive readout" aiGenerated aiLabel="AI-generated">
          <div className="space-y-2">
            <p className="text-sm leading-snug text-neutral-800">
              Global health holds at 29.7/100, below the desirable threshold and
              slightly down week over week, driven by authorized listing share.
            </p>
            <ol className="space-y-1.5" aria-label="Readout">
              {readoutInsights.map((it, i) => (
                <li key={it.title} className="flex gap-2.5">
                  <span className="w-4 shrink-0 text-sm font-bold tabular-nums text-primary-ink">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-neutral-800">{it.title}</div>
                    <div className="leading-snug">{it.summary}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Callout>
        <Callout tone="good" dense title="Priority action">
          Clean residual exposure on Best Buy
        </Callout>
        <Callout tone="neutral" dense>
          Illustrative concept values. Health Index proposal: 80% authorized
          listing share + 20% authorized seller share.
        </Callout>
      </div>
    ),
  },

  {
    id: "metric-strip",
    componentName: "MetricStrip",
    category: "analytics",
    title: "Metric Strip",
    subtitle: "Full-width band of 1-4 metric blocks — stacks on mobile",
    customLayout: true,
    render: () => (
      <MetricStrip
        title="Salud regional"
        subtitle="Ponderada por volumen de listings activos"
        items={regionStrip.map((r) => ({
          ...r,
          toneSpec: { thresholds: scoreBands },
          toneLabel: "banded",
        }))}
        note="Umbrales: verde ≥95 · amarillo 85-94 · rojo <85"
      />
    ),
  },

  // ── Maps ──────────────────────────────────────────────────
  {
    id: "south-america-map",
    componentName: "SouthAmericaMap",
    category: "maps",
    title: "South America Map",
    subtitle: "Country-level choropleth · error card if the boundaries can't load",
    fullHeight: 500,
    render: (h) => (
      <SouthAmericaMap data={saMapData} height={h} strings={{ ...DEMO_MAP_STRINGS, heading: "By country" }} />
    ),
  },
  {
    id: "chile-map",
    componentName: "ChileMap",
    category: "maps",
    title: "Chile Map",
    subtitle: "Region-level choropleth · error card if the boundaries can't load",
    fullHeight: 500,
    render: (h) => (
      <ChileMap data={clMapData} height={h} strings={{ ...DEMO_MAP_STRINGS, heading: "By region" }} />
    ),
  },

  // ── Transitions (motion system) ───────────────────────────
  //
  // The bi-components half of the Transitions gallery (plan §2.6):
  // tokens + the 3-level loading language. The dashboard-render
  // primitives (FadeSwap, NumberTween, RefreshOverlay,
  // HeightTransition) and the pattern demos (SWR cycle, skeleton→
  // content, drill in-place (swap, default) + drill accordion (opt-in),
  // chart data-change, anti-jump) are appended by dashboard-render's
  // DASHBOARD_RENDER_ENTRIES (merged by the builder's gallery page) — that package sits above this one in the
  // dependency graph. That is also where the table row-drill is
  // demonstrated: `DataTable` is a dashboard-render component, not a
  // bi-components widget, so it has no entry in THIS manifest.
  // Every entry renders through `TransitionDemo` (replay +
  // reduced-motion toggle + Used-in / How / Export metadata).
  {
    id: "motion-tokens",
    componentName: "MOTION",
    category: "transitions",
    title: "Motion Tokens",
    subtitle:
      "Durations {fast 120 · base 180 · slow 280}ms raced below, plus the entrance pair {delayEntrance 250 + durEntrance 1000} and the two easings — defined once, calibrate them here",
    customLayout: true,
    label: "MOTION",
    render: () => <DemoMotionTokens />,
  },
  {
    id: "spinner-levels",
    componentName: "Spinner",
    category: "transitions",
    title: "Spinners — Action Level",
    subtitle: "16/20px accent arc over the portal's light and dark backgrounds",
    customLayout: true,
    render: () => <DemoSpinnerLevels />,
  },
  {
    id: "global-progress-bar",
    componentName: "GlobalProgressBar",
    category: "transitions",
    title: "Global Progress Hairline",
    subtitle: "2px indeterminate magenta bar — the portal-wide \"something is fetching\" signal",
    customLayout: true,
    render: () => <DemoGlobalProgressBar />,
  },
  {
    id: "refresh-shimmer",
    componentName: "RefreshShimmer",
    category: "transitions",
    title: "Refresh Shimmer",
    subtitle: "Card-level sweep (CSS base of the RefreshOverlay wrapper)",
    customLayout: true,
    render: () => <DemoRefreshShimmer />,
  },
  {
    id: "entrance-grow",
    componentName: "useEntrance",
    category: "transitions",
    title: "Entrance — grow from zero",
    subtitle: "Meters, share bars, rings and columns rise from zero to their value on first paint (Replay remounts)",
    customLayout: true,
    render: () => <DemoEntranceGrow />,
  },
  {
    id: "entrance-everywhere",
    componentName: "EntranceGroup",
    category: "transitions",
    title: "Entrance — every widget, by default",
    subtitle: "Bars grow, pies pop, lines draw in (useLineDraw), density fades — default on all charts, opt-out via entrance={false}",
    customLayout: true,
    render: () => <DemoEntranceEverywhere />,
  },
  {
    id: "refetch-language",
    componentName: "useGrowCycle",
    category: "transitions",
    title: "Refetch language — the four things, together",
    subtitle:
      "What a filter change looks like: numbers MORPH · lines are REDRAWN · bars RE-GROW in one wave · the table does NOT re-cascade. Mount/Replay = first load (everything from zero); the button = refetch. Charts never crossfade (decisión 2026-07-23).",
    customLayout: true,
    label: "refetch language",
    render: () => <DemoRefetchLanguage />,
  },
  {
    id: "sparkle-hover",
    componentName: "ensureMotionStyles",
    category: "transitions",
    title: "Sparkle Hover — AI button",
    subtitle: "Accent-magenta hover fill + twinkling Sparkles icon (pmSparkle keyframe)",
    customLayout: true,
    render: () => <DemoSparkleHover />,
  },
  {
    id: "info-tip",
    componentName: "InfoTip",
    category: "transitions",
    title: "InfoTip — title tooltips",
    subtitle:
      "The ⓘ next to every explained title (sections, charts, tables, KPIs) — hover OR keyboard-focus shows the plain-language description. The bubble is portaled to <body>, so a clipping card can neither cut it nor cover it.",
    customLayout: true,
    overflowVisible: true,
    render: () => (
      <div className="space-y-3">
        {/* Light card title (ChartCard context) */}
        <div className="rounded-xl border border-neutral-200/80 bg-white px-5 py-3">
          <div className="flex items-center gap-1.5 text-neutral-800">
            <h3 className="text-sm font-bold truncate"># Storefronts per week</h3>
            <InfoTip
              className="text-neutral-400"
              text="Distinct sellers listing the brand's products each week, split by authorization status. NO = selling without a brand agreement."
            />
          </div>
        </div>
        {/* Dark table-card header (SectionHeader compact context — the
            dashboard SECTION band is light since 2026-09-10) */}
        <div className="rounded-xl bg-navy px-5 py-3">
          <div className="flex items-center gap-1.5 text-white">
            <h2 className="text-sm font-semibold">Marketplace comparison</h2>
            <InfoTip
              className="text-white/60"
              text="Last week vs the previous week and vs the average of the selected date range."
            />
          </div>
        </div>
        {/* `strong` variant (ronda 11): near-full resting opacity for
            ⓘs that must be findable at a glance — e.g. the KPI period
            tiles' corner ⓘ. Default stays the subdued 50%. */}
        <div className="rounded-xl border border-neutral-200/80 bg-white px-5 py-3">
          <div className="flex items-center gap-4 text-neutral-800">
            <span className="flex items-center gap-1.5 text-xs text-neutral-500">
              default
              <InfoTip className="text-neutral-400" text="Resting at 50% opacity — the historical subdued look." />
            </span>
            <span className="flex items-center gap-1.5 text-xs text-neutral-500">
              strong
              <InfoTip strong className="text-neutral-500" text="Resting at 90% opacity — findable at a glance without shouting." />
            </span>
          </div>
        </div>
        {/* THE case the portal exists for: a title inside a card that
            CLIPS (`overflow-hidden`, like every `fill` ChartCard) and
            sits next to a neighbour that used to paint over the
            bubble. Hovering either ⓘ must show the full text. */}
        <div className="grid grid-cols-2 gap-3">
          <div className="h-16 overflow-hidden rounded-xl border border-neutral-200/80 bg-white px-4 py-3">
            <div className="flex items-center gap-1.5 text-neutral-800">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide truncate">
                Unauthorized sellers
              </h3>
              <InfoTip
                className="text-neutral-400"
                text="Clipped card: the bubble escapes `overflow-hidden` because it renders in a portal on document.body, positioned with fixed coordinates."
              />
            </div>
            <p className="mt-1 text-lg font-bold text-neutral-900">6,349</p>
          </div>
          <div className="h-16 overflow-hidden rounded-xl border border-neutral-200/80 bg-white px-4 py-3">
            <div className="flex items-center gap-1.5 text-neutral-800">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide truncate">
                Neighbouring card
              </h3>
              <InfoTip
                className="text-neutral-400"
                text="Near the right edge the bubble clamps inside the viewport and the caret keeps pointing at its icon."
              />
            </div>
            <p className="mt-1 text-lg font-bold text-neutral-900">95.9%</p>
          </div>
        </div>
      </div>
    ),
  },
];

/**
 * Names from the barrel that are intentionally NOT in the gallery.
 * Used by the parity script to whitelist internal helpers / types.
 */
export const SHOWCASE_EXCLUDED = [
  "ReferenceSelect", // Reference theme adapter exercised by Select and table paging examples.
  "ColorThemeProvider", // Scoped configuration, demonstrated by client-themed consumers.
  "useColorTheme",
  // Motion plumbing (motion.jsx) — the visible motion exports (MOTION,
  // Spinner, GlobalProgressBar, RefreshShimmer) have entries above;
  // these are their internal support surface:
  //   ensureMotionStyles      — idempotent keyframe injection
  //   usePrefersReducedMotion — hook (no visual of its own; every
  //                             Transitions entry demos it via the
  //                             reduced-motion toggle)
  //   MotionConfig            — context override used by that toggle
  //   TransitionDemo          — the gallery scaffolding frame itself
  // CursorTip — internal portaled hover-readout helper (StackedShareBar
  // + MeterBar demo it live in their own entries).
  "CursorTip",
  //   useLineDraw — hook half of the line redraw; its visible surface is
  //   the line-family charts, the "entrance-everywhere" entry and the
  //   "refetch-language" entry (rule 2 of the refetch gesture).
  "useLineDraw",
  //   useCountUp — hook half of the value count-ups; visible surface is
  //   MeterBar/tile counts, the NumberTween gallery entry and the
  //   "refetch-language" entry (rule 1 of the refetch gesture).
  "useCountUp",
  //   lineDataSig — the cheap content signature both useLineDraw and
  //   useGrowCycle take as their redrawKey (same data ⇒ no replay).
  //   NOTE: `useGrowCycle` is NOT excluded — it carries the
  //   "refetch-language" entry, the card that demos the whole gesture.
  "lineDataSig",
  //   SuppressEntrance / useEntranceSuppressed — plumbing que apaga los
  //   entrances dentro de los ghosts de FadeSwap (snapshots settled).
  "SuppressEntrance",
  "useEntranceSuppressed",
  "usePrefersReducedMotion",
  "MotionConfig",
  "TransitionDemo",
  // Brand / palette tokens
  "PALETTE",
  "NEUTRAL",
  "SEMANTIC",
  "HEAT_RAMP",
  // Tone vocabulary (epic v3 scorecards) — pure helpers + a token
  // list, not widgets. Their VISIBLE surface is every scorecard
  // primitive that takes a `toneSpec` (MeterBar, StatusChip, …), and
  // those carry the gallery entries that demonstrate the bands.
  "TONES",
  "resolveTone",
  "toneStyle",
  "toneLabelKey",
  // Pure share math behind StackedShareBar (that entry demos it).
  "normalizeSegments",
  // Wrappers + low-level helpers (rendered via the components above)
  "ChartCard",
  "ChartLegend",
  "ChartEmpty",
  "MultiSeriesTooltip",
  // Measured card-chrome constants (uniform-grid contract) — consumed
  // by dashboard-render's GenericChart, not standalone widgets.
  "CHART_CARD_CHROME_PX",
  "MAP_CARD_CHROME_PX",
  // Delta-chip primitive (rule 4) — rendered inside KPIs / tables,
  // not a standalone widget.
  "DeltaChip",
  // Trend-matrix cell plumbing — the strict "value ▲/▼ ±x.x%" cell
  // parser + the bare "▲ +x.x%" delta-only parser (default cells) +
  // the cell primitive + its color tokens. Rendered inside
  // TrendMatrixTable (showcased above) and the dashboard-render
  // DataTable / table-chart cell renderers; not standalone widgets.
  "parseValueDelta",
  "parseDeltaOnly",
  "TrendCell",
  "trendMetricColor",
  "TREND_DOT_COLORS",
  "TREND_BAR_NEUTRAL",
  // Pure utility functions
  "fmt",
  "fmtN",
  "pct",
  "formatValue",
  "resolveFormatter",
  "resolveLabels",
  "resolveSentiment",
  "lastDelta",
  "trendClasses",
  "hasData",
  "useContainerWidth",
  "monotonePath",
  "tintColor",
  "thinLabelIndices",
  "xLabelStride",
  "fitSlotLabel",
  "SlotLabel",
  "peakLabelIndices",
  // Adaptive label gutter + 2-line wrap for the ranked horizontal
  // bars (feedback 2026-07-14) — pure layout helper + its glyph-width
  // constants, rendered inside HorizontalBar / HorizontalBarT (their
  // showcase entries demo the wrap with a long SKU label).
  "barLabelLayout",
  // pieCenterX — pure layout helper of PieChart / PieChartT (centres
  // the pie + legend block in a wide card); its visible surface is the
  // two Pie entries.
  "pieCenterX",
  // heatAxisLabels — pure layout helper of HeatmapChart (truncation +
  // longest-label measure the axis channels size from); its visible
  // surface is the Heatmap entry, which now rotates crowded x labels.
  "heatAxisLabels",
  //   BAR_DENSITY / barDensity — la métrica de fila de las barras
  //   rankeadas, una sola vez para la variante estática y la
  //   interactiva. Su superficie visible es la entrada "Horizontal
  //   Bars", que demuestra las DOS densidades una al lado de la otra.
  "BAR_DENSITY",
  "barDensity",
  "BAR_LABEL_CHAR_PX",
  "BAR_LABEL_WRAP_CHAR_PX",
  // Axis polish 2026-07 — round tick math + label anti-collision +
  // density-adaptive line markers. Pure helpers rendered inside the
  // charts above, not standalone widgets.
  "niceStep",
  "niceTicks",
  "niceSymmetricTicks",
  "separateLabelYs",
  "lineDotRadius",
  // Legend-table plumbing (redesign rule 3) — rendered inside charts,
  // not a standalone widget.
  "legendLayout",
  "SVGLegendRows",
  // Internal tooltip plumbing used by interactive chart variants —
  // not user-facing widgets, no standalone showcase.
  "useTooltip",
  "Tooltip",
  "ChartSVGWrap",
];
